import axios from 'axios';
import { format } from 'date-fns';

import { ApiResponse } from '../../models/api.response.model';
import { Error } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { Company } from '../../models/company.model';
import { Invoice } from '../../models/invoice.model';
import { CompanyInvoices } from '../../models/company.invoices.model';
import { authToken } from '../../scripts/authentication.script';
import { convertRequirementsNumbers } from '../../scripts/api.payload.script';

const OBJECT_ID_REGEX = /^[a-fA-F0-9]{24}$/;

export class InvoiceDA {
  static apiResponse: ApiResponse

  private static validateCompanyId(id_company: string): ApiResponse | null {
    if (!id_company || !OBJECT_ID_REGEX.test(id_company)) {
      return {
        status: 400,
        message: 'Bad request',
        data: { code: 'INVALID_COMPANY_ID', message: `Invalid company ID: "${id_company ?? ''}"` }
      }
    }
    return null
  }

  public static async getInvoices(
    id_company: string,
    interval: string,
    start_date: string,
    end_date: string,
    contact_id: string,
    validity: string,
    status: string,
    expiration: string,
    payment: string,
    sort_field: string,
    sort_mode: string,
    group_by: string,
    page: number = 1,
    limit: number = 20,
    search: string = '',
    all: boolean = false
  ): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyInvoiceList: CompanyInvoices | null = null
      let companyInvoiceGroup: CompanyInvoices | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoices.list.data.json').then(response => { companyInvoiceList = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/sale.invoices.group.data.json').then(response => { companyInvoiceGroup = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyInvoiceList!.id_company === id_company || companyInvoiceGroup!.id_company === id_company) {
        if (group_by.length === 0) {
          companyInvoiceList!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, validity: validity, status: status, expiration: expiration, payment: payment, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        } else {
          companyInvoiceGroup!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, validity: validity, status: status, expiration: expiration, payment: payment, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: (group_by.length === 0) ? companyInvoiceList : companyInvoiceGroup
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let startOfDay: Date = new Date(new Date().setHours(0, 0, 0, 0))
      let endOfDay: Date = new Date(new Date().setHours(23, 59, 59, 999))
      const params = new URLSearchParams()
      if (interval) params.append('interval', interval)
      if (start_date) params.append('start_date', start_date)
      if (end_date) params.append('end_date', end_date)
      if (contact_id) params.append('id_contact', contact_id)
      if (validity === 'valid') params.append('validated', 'true')
      else if (validity === 'invalid') params.append('validated', 'false')
      if (status) params.append('status', status)
      if (expiration) params.append('expiration', expiration)
      if (payment) params.append('payment', payment)
      if (sort_field) params.append('sort_by', sort_field)
      if (sort_mode) params.append('sort_order', sort_mode)
      if (group_by) params.append('group_by', group_by)
      if (search) params.append('search', search)
      params.append('start_day', String(startOfDay))
      params.append('end_day', String(endOfDay))
      if (all) {
        params.append('all', 'true')
      } else {
        params.append('page', String(page))
        params.append('limit', String(limit))
      }
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices?${params.toString()}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getInvoice(
    id_company: string,
    id_invoice: string
  ): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (invoice!.id === id_invoice) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postInvoice(
    id_company: string,
    customer: any,
    concerning: string,
    date: string,
    valid_until: string,
    currency: any,
    foreign_currency: any,
    treasury_account: any,
    language: string,
    qr_type: string,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string }[] }[],
    signatures: string[],
    company_detail: { uuid: string, name: string, logo: string, address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string } },
    reference: string,
    dunning_policy_id?: string | null,
    voucher_document_ids?: string[]
  ): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        concerning: concerning,
        signatures: signatures,
        company: company_detail,
        customer: customer,
        valid_until: valid_until,
        currency: currency,
        foreign_currency: foreign_currency,
        treasury_account: treasury_account,
        requirements: convertRequirementsNumbers(requirements),
        created_date: date,
        language: language,
        qr_type: qr_type,
        reference: reference
      }
      data.dunning_policy_id = dunning_policy_id ?? null
      if (voucher_document_ids && voucher_document_ids.length > 0) {
        data.voucher_document_ids = voucher_document_ids
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postInvoiceReference(
    id_company: string,
    id_order: string
  ): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (invoice!.id_orders?.some((o: any) => (typeof o === 'object' ? o.id : o) === id_order) || invoice!.order?.id === id_order) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_order}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.post(url, null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postInvoiceReminder(
    id_company: string,
    id_invoice: string,
    description: string,
    amount: string
  ): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (invoice!.id === id_invoice) {
        invoice!.status = 'reminder'
        invoice!.reminders.push({
          id: `reminder${(Math.floor(Math.random() * 1000)).toString()}`,
          description: description,
          amount: (amount.length > 0) ? Number(amount) : 0,
          created_at: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`,
          updated_at: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`
        })

        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/reminders`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        description: description,
        amount: Number(amount)
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putInvoice(
    id_company: string,
    id_invoice: string,
    concerning: string,
    date: string,
    valid_until: string,
    currency: any,
    foreign_currency: any,
    treasury_account: any,
    language: string,
    qr_type: string,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string }[] }[],
    signatures: string[],
    dunning_policy_id: string | null = null,
    voucher_document_ids?: string[]
  ): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (invoice!.id === id_invoice) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        concerning: concerning,
        signatures: signatures,
        requirements: convertRequirementsNumbers(requirements),
        valid_until: valid_until,
        language: language,
        qr_type: qr_type,
        currency: currency,
        foreign_currency: foreign_currency,
        treasury_account: treasury_account,
        created_date: date,
        dunning_policy_id: dunning_policy_id ?? null
      }
      if (voucher_document_ids && voucher_document_ids.length > 0) {
        data.voucher_document_ids = voucher_document_ids
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putInvoiceStatus(
    id_company: string,
    id_invoice: string,
    amount: number | null,
    _company_tax: { subject_tax: boolean, tax_method: string, tax_declaration: string, tax_period: string, tax_statement: string, language: string } | null,
    content_accounts: { id_content: string, id_account_credit: string, id_account_debit: string }[] | null,
    foreign_currency?: any
  ): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (invoice!.id === id_invoice) {
        invoice!.validated = (invoice!.validated) ? false : true

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/status`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        amount: amount,
        content_accounts: content_accounts,
        foreign_currency: foreign_currency ?? null
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putInvoiceReminder(
    id_company: string,
    id_invoice: string,
    id_reminder: string,
    description: string,
    amount: string
  ): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (invoice!.reminders.some(item => item.id === id_reminder)) {
        invoice!.reminders = invoice!.reminders.map(item => {
          if (item.id === id_reminder) {
            item.description = description
            item.amount = Number(amount)
            item.updated_at =`${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`
          }
          return item
        })

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/reminders/${id_reminder}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        description: description,
        amount: Number(amount)
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async createInvoiceFromOrders(
    id_company: string,
    order_ids: string[]
  ): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 201,
        message: 'Success',
        data: { id: 'dev-invoice-id', invoice_number: 'INV-DEV-0001' }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        order_ids: order_ids
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async deleteInvoiceReminder(
    id_company: string,
    id_invoice: string,
    id_reminder: string
  ): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let invoice: Invoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/sale.invoice.data.json').then(response => { invoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (invoice!.reminders.some(item => item.id === id_reminder)) {
        invoice!.reminders = invoice!.reminders.filter(item => item.id !== id_reminder)

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: invoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/reminders/${id_reminder}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.delete(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async attachVoucher(id_company: string, id_invoice: string, body: { id_document_inbox: string, role?: string }): Promise<any> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/vouchers`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, body, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async uploadVoucher(id_company: string, id_invoice: string, file: File, role?: string): Promise<any> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/vouchers/upload`
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source', 'web')
      if (role) formData.append('role', role)
      let config: any = { headers: { Authorization: authToken(), 'Content-Type': 'multipart/form-data' } }
      const response = await axios.post(url, formData, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async deleteVoucher(id_company: string, id_invoice: string, index: number): Promise<any> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/vouchers/${index}`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async deleteInvoice(id_company: string, id_invoice: string): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data ?? {} }
    }
  }

  public static async createPartialInvoice(
    id_company: string,
    id_order: string,
    body: { type: 'lines' | 'deposit'; lines?: { requirement_index: number; content_index: number; quantity: number }[]; amount?: number; description?: string }
  ): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/from-order/${id_order}/partial`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, body, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data ?? {} }
    }
  }

  public static async getOrderInvoicingStatus(id_company: string, id_order: string): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/orders/${id_order}/invoicing-status`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.get(url, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data ?? {} }
    }
  }

  public static async revertToDraft(id_company: string, id_invoice: string): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/revert-draft`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.put(url, {}, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data ?? {} }
    }
  }

  public static async suspendDunning(id_company: string, id_invoice: string, suspended_until?: string | null): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/dunning/suspend`
    let config: any = { headers: { Authorization: authToken() } }
    let data: any = suspended_until ? { suspended_until } : {}
    await axios.patch(url, data, config).then(response => {
      this.apiResponse = { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    }).catch(error => {
      this.apiResponse = { status: error.response?.status ?? 500, message: error.response?.statusText ?? 'Error', data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message } }
    })
    return this.apiResponse
  }

  public static async resumeDunning(id_company: string, id_invoice: string): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/dunning/resume`
    let config: any = { headers: { Authorization: authToken() } }
    await axios.patch(url, {}, config).then(response => {
      this.apiResponse = { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    }).catch(error => {
      this.apiResponse = { status: error.response?.status ?? 500, message: error.response?.statusText ?? 'Error', data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message } }
    })
    return this.apiResponse
  }

  public static async stopDunning(id_company: string, id_invoice: string): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/dunning/stop`
    let config: any = { headers: { Authorization: authToken() } }
    await axios.patch(url, {}, config).then(response => {
      this.apiResponse = { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    }).catch(error => {
      this.apiResponse = { status: error.response?.status ?? 500, message: error.response?.statusText ?? 'Error', data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message } }
    })
    return this.apiResponse
  }

  public static async patchAccounts(id_company: string, id_invoice: string, content_accounts: {id_content: string, id_account_debit: string, id_account_credit: string}[], company?: {subject_tax: boolean, tax_method: string, tax_declaration: string, tax_period: string, tax_statement: string, language: string} | null): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/accounts`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.patch(url, { content_accounts, company: company ?? undefined }, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data ?? {} }
    }
  }

  public static async postWriteOff(id_company: string, id_invoice: string, data: { amount: number, date: string, reason: string }): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/write-off`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, data, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data?.error ?? error.response?.data ?? {} }
    }
  }

  public static async deleteWriteOff(id_company: string, id_invoice: string, id_write_off: string): Promise<ApiResponse> {
    const guardError = InvoiceDA.validateCompanyId(id_company)
    if (guardError) return guardError

    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/invoices/${id_invoice}/write-off/${id_write_off}`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data?.error ?? error.response?.data ?? {} }
    }
  }
}
